import { useAuthToken } from '@/services/auth';
import { usePlatformStore } from '@/store/platform';
import { useProjectStore } from '@/store/project';
import { apiBasePath, coreApiBasePath } from '@/utils/apiBasePath';
import { http } from '@/utils/http';
import type {
  ConciergeCandidatesApiApproveConciergeCandidateRequest,
  ConciergeCandidatesApiDeleteConciergeCandidateRequest,
  ConciergeCandidatesApiExcludeEmailDomainRequest,
  ConciergeCandidatesApiGetConciergeCandidateRequest,
  ConciergeCandidatesApiListConciergeCandidatesRequest,
  CreatePixelRequest,
  CustomersApiCreateCustomerChatChannelRequest,
  CustomersApiCreateCustomerDataDeletionRequestRequest,
  CustomersApiExportCustomersRequest,
  CustomersApiGetCustomerDataDeletionRequestRequest,
  CustomersApiGetCustomerRequest,
  CustomersApiListCustomersRequest,
  CustomersApiListCustomerTagsRequest,
  CustomersApiUpdateCustomerRequest,
  DashboardApiGetDashboardRequest,
  DashboardApiSaveDashboardRequest,
  EnquiriesApiCreateCustomerEnquiryChatChannelRequest,
  EnquiriesApiGetCustomerEnquiryRequest,
  EnquiriesApiListCustomerEnquiriesRequest,
  EnquiriesApiListCustomerEnquiryLogsRequest,
  EnquiriesApiUpdateCustomerEnquiryRequest,
  OrdersApiAmendOrderRequest,
  OrdersApiConfirmOrderRequest,
  OrdersApiCreateOrderChatChannelRequest,
  OrdersApiCreateOrderRequest,
  OrdersApiExportOrdersRequest,
  OrdersApiGetFulfillmentRequest,
  OrdersApiGetOrderReceiptRequest,
  OrdersApiGetOrderRequest,
  OrdersApiListAvailableFulfillersRequest,
  OrdersApiListOrdersRequest,
  OrdersApiRetryPlatformPaymentRequest,
  OrdersApiSendOrderReceiptRequest,
  OrdersApiUpdateFulfillmentRequest,
  OrdersApiUpdateOrderRequest,
  OrdersApiUpdatePlatformFulfillmentStyleApplicationRequest,
  PlatformLogo,
  ReviewsApiExportReviewsRequest,
  ReviewsApiGetReviewRequest,
  ReviewsApiListReviewsRequest,
  ReviewsApiModerateReviewRequest,
  Terms,
  TermsApiGetTermsRequest,
  TermsApiSaveTermsRequest,
  UpdateModerationItemRequest,
  UpdatePlatformRequest,
} from '@teemill/platform';
import {
  ConciergeCandidatesApi,
  Configuration,
  CustomersApi,
  DashboardApi,
  EnquiriesApi,
  ModerationApi,
  OrdersApi,
  PaymentApi,
  PixelsApi,
  PlatformApi,
  ReviewsApi,
  TermsApi,
} from '@teemill/platform';
import { watchImmediate, watchOnce } from '@vueuse/core';
import { computed } from 'vue';
import { useProject } from './projects';

export interface PublicPlatform {
  id: string;
  name: string;
  public: boolean;
  domains: string[];
  logos: PlatformLogo[];
  favicon: string | null;
  stripePaymentAccountId: string | null;
  terms: Terms;
  hasCustomDashboard: boolean;
  projectId: string;
}

export const usePlatform = () => {
  const config = new Configuration({
    basePath: apiBasePath(),
    accessToken: async () => useAuthToken(),
    baseOptions: {
      headers: {
        'User-Agent': null,
      },
    },
  });

  const platformClient = new PlatformApi(config);
  const ordersClient = new OrdersApi(config);
  const customersClient = new CustomersApi(config);
  const paymentClient = new PaymentApi(config);
  const reviewsClient = new ReviewsApi(config);
  const termsClient = new TermsApi(config);
  const enquiriesClient = new EnquiriesApi(config);
  const dashboardClient = new DashboardApi(config);
  const pixelsClient = new PixelsApi(config);
  const moderationClient = new ModerationApi(config);
  const conciergeCandidatesClient = new ConciergeCandidatesApi(config);

  const platformStore = usePlatformStore();
  const project = useProject();

  const getPublicPlatform = async (projectId?: string) => {
    const response = await http.get(`${coreApiBasePath()}/omnis/v3/platform`, {
      params: {
        ...(projectId && { project: projectId }),
      },
    });

    return response.data as PublicPlatform;
  };

  const getPlatformTerms = async (platformId?: string, projectId?: string) => {
    const response = await http.get(`${coreApiBasePath()}/omnis/v3/platform/active-terms`, {
      params: {
        ...(platformId && { project: projectId }),
      },
    });

    return response.data;
  };

  const signPlatformTerms = async (platformId?: string, projectId?: string, termsId?: string) => {
    const response = await http.post(
      `${coreApiBasePath()}/omnis/v3/platform/active-terms`,
      {
        termsId,
      },
      {
        params: {
          ...(projectId && { project: projectId }),
        },
      },
    );

    return response.data;
  };

  const logSearchTerm = async (
    projectId?: string,
    searchTerm?: string,
    searchResultNumber?: number,
    searchLogContext?: string,
  ) => {
    await http.post(
      `${coreApiBasePath()}/omnis/v3/platform/search-log`,
      {
        searchTerm,
        searchResultNumber,
        searchLogContext,
      },
      {
        params: {
          ...(projectId && { project: projectId }),
        },
      },
    );
  };

  const getPublicPlatformDashboardItems = async (platformId?: string, projectId?: string) => {
    const response = await http.get(`${coreApiBasePath()}/omnis/v3/platform/dashboard`, {
      params: {
        ...(platformId && { project: projectId }),
      },
    });

    return response.data;
  };

  const getPublicProjectId = () =>
    project.value?.id || new URLSearchParams(window.location.search).get('project') || undefined;

  return {
    domains: {
      list: () =>
        platformClient.listDomains({
          project: project.value!.id,
        }),
      create: (domain: string) =>
        platformClient.createDomain({
          project: project.value!.id,
          createDomainRequest: {
            domain,
          },
        }),
      delete: (domain: string) =>
        platformClient.deleteDomain({
          project: project.value!.id,
          domain,
        }),
    },
    client: {
      domains: {
        list: () => platformClient.listClientDomains({ project: project.value!.id }),
        create: (domain: string) =>
          platformClient.createClientDomain({
            project: project.value!.id,
            createDomainRequest: {
              domain,
            },
          }),
        delete: (domain: string) =>
          platformClient.deleteClientDomain({
            project: project.value!.id,
            domain,
          }),
      },
    },
    paymentClient,
    paymentAccount: {
      stripe: {
        get: (projectId: string) =>
          paymentClient.getStripePaymentAccount({ project: projectId }).then(({ data }) => data),
        authorize: (projectId: string) =>
          paymentClient.authorizeStripe({ project: projectId }).then(({ data }) => data),
        deauthorize: (projectId: string) =>
          paymentClient.deauthorizeStripe({ project: projectId }).then(({ data }) => data),
      },
    },
    active: computed(() => platformStore.platform),

    // Platform operations
    get: () =>
      project.value ? platformClient.getPlatform({ project: project.value.id }) : undefined,
    update: (updatePlatformRequest: UpdatePlatformRequest) =>
      project.value
        ? platformClient.updatePlatform({ project: project.value.id, updatePlatformRequest })
        : undefined,
    public: {
      get: () => getPublicPlatform(getPublicProjectId()),
      getLemonstand: getPublicPlatform,
      getActiveTerms: () => getPlatformTerms(platformStore.platform?.id, getPublicProjectId()),
      signTerms: (platformId?: string, projectId?: string, termsId?: string) =>
        signPlatformTerms(platformId, projectId, termsId),
      getPublicDashboardItems: () =>
        getPublicPlatformDashboardItems(platformStore.platform?.id, getPublicProjectId()),
    },
    // Orders operations
    getOrder: (options: Omit<OrdersApiGetOrderRequest, 'project'>) =>
      ordersClient.getOrder({
        project: project.value!.id,
        ...options,
      }),
    listOrders: (options: Omit<OrdersApiListOrdersRequest, 'project'>) =>
      ordersClient.listOrders({
        project: project.value!.id,
        ...options,
      }),
    exportOrders: (options: Omit<OrdersApiExportOrdersRequest, 'project'>) =>
      ordersClient.exportOrders({
        project: project.value!.id,
        ...options,
      }),
    getOrderReceipt: (options: Omit<OrdersApiGetOrderReceiptRequest, 'project'>) =>
      ordersClient.getOrderReceipt({
        project: project.value!.id,
        ...options,
      }),
    sendOrderReceipt: (options: Omit<OrdersApiSendOrderReceiptRequest, 'project'>) =>
      ordersClient.sendOrderReceipt({
        project: project.value!.id,
        ...options,
      }),
    retryOrdersPlatformPayment: (options: Omit<OrdersApiRetryPlatformPaymentRequest, 'project'>) =>
      ordersClient.retryPlatformPayment({
        project: project.value!.id,
        ...options,
      }),
    getFulfillment: (options: Omit<OrdersApiGetFulfillmentRequest, 'project'>) =>
      project.value
        ? ordersClient.getFulfillment({
            project: project.value.id,
            ...options,
          })
        : undefined,
    updateFulfillment: (options: Omit<OrdersApiUpdateFulfillmentRequest, 'project'>) =>
      project.value
        ? ordersClient.updateFulfillment({
            project: project.value.id,
            ...options,
          })
        : undefined,
    listAvailableFulfillers: (options: Omit<OrdersApiListAvailableFulfillersRequest, 'project'>) =>
      project.value
        ? ordersClient.listAvailableFulfillers({
            project: project.value.id,
            ...options,
          })
        : undefined,
    createOrder: (options: Omit<OrdersApiCreateOrderRequest, 'project'>) =>
      ordersClient.createOrder({
        project: project.value!.id,
        ...options,
      }),
    confirmOrder: (options: Omit<OrdersApiConfirmOrderRequest, 'project'>) =>
      ordersClient.confirmOrder({
        project: project.value!.id,
        ...options,
      }),
    amendOrder: (options: Omit<OrdersApiAmendOrderRequest, 'project'>) =>
      ordersClient.amendOrder({
        project: project.value!.id,
        ...options,
      }),
    updateOrder: (options: Omit<OrdersApiUpdateOrderRequest, 'project'>) =>
      ordersClient.updateOrder({
        project: project.value!.id,
        ...options,
      }),
    updateApplication: (
      options: Omit<OrdersApiUpdatePlatformFulfillmentStyleApplicationRequest, 'project'>,
    ) =>
      ordersClient.updatePlatformFulfillmentStyleApplication({
        project: project.value!.id,
        ...options,
      }),
    createOrderChatChannel: (options: Omit<OrdersApiCreateOrderChatChannelRequest, 'project'>) =>
      ordersClient.createOrderChatChannel({
        project: project.value!.id,
        ...options,
      }),
    // Customers operations
    getCustomer: (options: Omit<CustomersApiGetCustomerRequest, 'project'>) =>
      project.value
        ? customersClient.getCustomer({
            project: project.value.id,
            ...options,
          })
        : undefined,
    updateCustomer: (options: Omit<CustomersApiUpdateCustomerRequest, 'project'>) =>
      project.value
        ? customersClient.updateCustomer({
            project: project.value.id,
            ...options,
          })
        : undefined,
    listCustomers: (options: Omit<CustomersApiListCustomersRequest, 'project'>) =>
      project.value
        ? customersClient.listCustomers({
            project: project.value.id,
            ...options,
          })
        : undefined,
    listCustomerTags: (options: Omit<CustomersApiListCustomerTagsRequest, 'project'>) =>
      project.value
        ? customersClient.listCustomerTags({
            project: project.value.id,
            ...options,
          })
        : undefined,
    exportCustomers: (options: Omit<CustomersApiExportCustomersRequest, 'project'>) =>
      project.value
        ? customersClient.exportCustomers({
            project: project.value.id,
            ...options,
          })
        : undefined,
    createCustomerChatChannel: (
      options: Omit<CustomersApiCreateCustomerChatChannelRequest, 'project'>,
    ) =>
      customersClient.createCustomerChatChannel({
        project: project.value!.id,
        ...options,
      }),
    createCustomerDataDeleteRequest: (
      options: Omit<CustomersApiCreateCustomerDataDeletionRequestRequest, 'project'>,
    ) =>
      customersClient.createCustomerDataDeletionRequest({
        project: project.value!.id,
        ...options,
      }),
    getCustomerDataDeletionRequest: (
      options: Omit<CustomersApiGetCustomerDataDeletionRequestRequest, 'project'>,
    ) =>
      customersClient.getCustomerDataDeletionRequest({
        project: project.value!.id,
        ...options,
      }),
    // Reviews operations
    listReviews: (options: Omit<ReviewsApiListReviewsRequest, 'project'>) =>
      project.value
        ? reviewsClient.listReviews({
            project: project.value.id,
            ...options,
          })
        : undefined,
    getReview: (options: Omit<ReviewsApiGetReviewRequest, 'project'>) =>
      project.value
        ? reviewsClient.getReview({
            project: project.value.id,
            ...options,
          })
        : undefined,
    moderateReview: (options: Omit<ReviewsApiModerateReviewRequest, 'project'>) =>
      project.value
        ? reviewsClient.moderateReview({
            project: project.value.id,
            ...options,
          })
        : undefined,
    exportReviews: (options: Omit<ReviewsApiExportReviewsRequest, 'project'>) =>
      project.value
        ? reviewsClient.exportReviews({
            project: project.value.id,
            ...options,
          })
        : undefined,
    // Enquiries operations
    listEnquiries: (options: Omit<EnquiriesApiListCustomerEnquiriesRequest, 'project'>) =>
      project.value
        ? enquiriesClient.listCustomerEnquiries({
            project: project.value.id,
            ...options,
          })
        : undefined,
    getEnquiry: (options: Omit<EnquiriesApiGetCustomerEnquiryRequest, 'project'>) =>
      project.value
        ? enquiriesClient.getCustomerEnquiry({
            project: project.value.id,
            ...options,
          })
        : undefined,
    listEnquiryLogs: (options: Omit<EnquiriesApiListCustomerEnquiryLogsRequest, 'project'>) =>
      project.value
        ? enquiriesClient.listCustomerEnquiryLogs({
            project: project.value.id,
            ...options,
          })
        : undefined,
    createEnquiryChatChannel: (
      options: Omit<EnquiriesApiCreateCustomerEnquiryChatChannelRequest, 'project'>,
    ) =>
      project.value
        ? enquiriesClient.createCustomerEnquiryChatChannel({
            project: project.value.id,
            ...options,
          })
        : undefined,
    updateEnquiry: (options: Omit<EnquiriesApiUpdateCustomerEnquiryRequest, 'project'>) =>
      project.value
        ? enquiriesClient.updateCustomerEnquiry({
            project: project.value.id,
            ...options,
          })
        : undefined,
    // Terms operations
    getTerms: (options: Omit<TermsApiGetTermsRequest, 'project'>) =>
      project.value
        ? termsClient.getTerms({
            project: project.value.id,
            ...options,
          })
        : undefined,
    saveTerms: (options: Omit<TermsApiSaveTermsRequest, 'project'>) =>
      project.value
        ? termsClient.saveTerms({
            project: project.value.id,
            ...options,
          })
        : undefined,
    // Pixels operations
    pixels: {
      list: (
        platformId: string,
        search?: string | undefined,
        pageSize?: number | undefined,
        pageToken?: number | undefined,
      ) =>
        project.value
          ? pixelsClient
              .listPixels({
                project: project.value.id,
                platformId,
                search,
                pageSize,
                pageToken,
              })
              .then(({ data }) => data)
          : undefined,
      get: (platformId: string, pixelId: string) =>
        project.value
          ? pixelsClient
              .getPixel({
                project: project.value.id,
                platformId,
                pixelId,
              })
              .then(({ data }) => data)
          : undefined,
      create: (platformId: string, payload: CreatePixelRequest) =>
        project.value
          ? pixelsClient
              .createPixel({
                project: project.value.id,
                platformId,
                createPixelRequest: payload,
              })
              .then(({ data }) => data)
          : undefined,
      update: (platformId: string, pixelId: string, payload: CreatePixelRequest) =>
        project.value
          ? pixelsClient
              .updatePixel({
                project: project.value.id,
                platformId,
                pixelId,
                createPixelRequest: payload,
              })
              .then(({ data }) => data)
          : undefined,
      delete: (platformId: string, pixelId: string) =>
        project.value
          ? pixelsClient
              .deletePixel({
                project: project.value.id,
                platformId,
                pixelId,
              })
              .then(({ data }) => data)
          : undefined,
    },
    // Search Log operations
    logSearch: (searchTerm?: string, searchResultNumber?: number, searchLogContext?: string) =>
      project.value
        ? logSearchTerm(project.value.id, searchTerm, searchResultNumber, searchLogContext)
        : logSearchTerm(undefined, searchTerm, searchResultNumber, searchLogContext),
    //Platform dashboard operations
    getPlatformDashboardItems: (options: Omit<DashboardApiGetDashboardRequest, 'project'>) =>
      project.value
        ? dashboardClient.getDashboard({
            project: project.value.id,
            ...options,
          })
        : undefined,
    savePlatformDashboard: (options: Omit<DashboardApiSaveDashboardRequest, 'project'>) =>
      project.value
        ? dashboardClient.saveDashboard({
            project: project.value.id,
            ...options,
          })
        : undefined,
    // Concierge Candidates operations
    listConciergeCandidates: (
      options: Omit<ConciergeCandidatesApiListConciergeCandidatesRequest, 'project'>,
    ) =>
      project.value
        ? conciergeCandidatesClient.listConciergeCandidates({
            project: project.value.id,
            ...options,
          })
        : undefined,
    getConciergeCandidate: (
      options: Omit<ConciergeCandidatesApiGetConciergeCandidateRequest, 'project'>,
    ) =>
      project.value
        ? conciergeCandidatesClient.getConciergeCandidate({
            project: project.value.id,
            ...options,
          })
        : undefined,
    approveConciergeCandidate: (
      options: Omit<ConciergeCandidatesApiApproveConciergeCandidateRequest, 'project'>,
    ) =>
      project.value
        ? conciergeCandidatesClient.approveConciergeCandidate({
            project: project.value.id,
            ...options,
          })
        : undefined,
    deleteConciergeCandidate: (
      options: Omit<ConciergeCandidatesApiDeleteConciergeCandidateRequest, 'project'>,
    ) =>
      project.value
        ? conciergeCandidatesClient
            .deleteConciergeCandidate({
              project: project.value.id,
              ...options,
            })
            .then(({ data }) => data)
        : undefined,
    excludeEmailDomain: (
      options: Omit<ConciergeCandidatesApiExcludeEmailDomainRequest, 'project'>,
    ) =>
      project.value
        ? conciergeCandidatesClient.excludeEmailDomain({
            project: project.value.id,
            ...options,
          })
        : undefined,
    // Moderation operations
    moderation: {
      update: (moderationItemId: string, payload: UpdateModerationItemRequest) =>
        project.value
          ? moderationClient
              .updateModerationItem({
                project: project.value.id,
                moderationItemId: moderationItemId,
                updateModerationItemRequest: payload,
              })
              .then(({ data }) => data)
          : undefined,
    },
  };
};

const isAllowedHostname = (platformDomains: string[]): boolean => {
  const hostname = window.location.hostname;

  const allowedHostnames = [
    'podos.io',
    'cloud.teemill.com',
    'cloud.printondemandsoftware.com',
    'cloud.localhost',
    'localhost',
    ...platformDomains,
  ];

  if (allowedHostnames.includes(hostname)) {
    return true;
  }

  if (hostname.includes('tunnel.teemill.com') || hostname.includes('tunnel.podos.io')) {
    return true;
  }

  return false;
};

export const platform = {
  async install() {
    const platformStore = usePlatformStore();
    const projectStore = useProjectStore();

    const platformService = usePlatform();

    watchOnce(
      () => projectStore.ready,
      () => {
        watchImmediate(
          () => projectStore.active,
          (activeProject) => {
            platformService.public
              .get()
              .then((platform) => {
                if (activeProject && !isAllowedHostname(platform.domains)) {
                  window.location.hostname = platform.domains[0] || 'podos.io';
                }

                platformStore.update(platform);
              })
              .catch(() => {
                platformStore.update(undefined);
              });
          },
        );
      },
    );
  },
};
